'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORE_STORAGE_KEY = 'erp_current_store';

export interface CurrentStore {
  id: string;
  name: string;
}

interface StoreContextValue {
  currentStore: CurrentStore | null;
  setCurrentStore: (store: CurrentStore | null) => void;
  availableStores: CurrentStore[];
  setAvailableStores: (stores: CurrentStore[]) => void;
}

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [currentStore, setCurrentStoreState] = useState<CurrentStore | null>(null);
  const [availableStores, setAvailableStores] = useState<CurrentStore[]>([]);

  const setCurrentStore = useCallback((store: CurrentStore | null) => {
    setCurrentStoreState(store);
    if (typeof window !== 'undefined') {
      if (store) {
        localStorage.setItem(STORE_STORAGE_KEY, JSON.stringify(store));
      } else {
        localStorage.removeItem(STORE_STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORE_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as CurrentStore;
        if (parsed?.id && parsed?.name) setCurrentStoreState(parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  const value: StoreContextValue = {
    currentStore,
    setCurrentStore,
    availableStores,
    setAvailableStores,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}
